import { useEffect, useRef } from "react";
import { supabase } from "../../lib/supabase";
import { logger } from "../../lib/logger";
import { useData } from "../useData";
import { useAuth } from "../../context/AuthContext";
import { useRBAC } from "../useRBAC";
import { roleConfig } from "../../config/roleConfig";
import { getPartsInTZ, startOfDayInTZ, endOfDayInTZ, toOrgDateKey } from "../../lib/timezone";
import { GLOBAL_DATE_RANGE_KEY, DATE_RANGE_CHANGE_EVENT } from "../useGlobalDateRange";
import {
  getSetterIdsCache,
  setSetterIdsCache,
  getMetricsCache,
  setMetricsCache,
  getSessionTimelineCache,
  setSessionTimelineCache,
  setPendingFetch,
  getDailyRowsCache,
  setDailyRowsCache,
  getSetterNamesCache,
  setSetterNamesCache,
} from "../../lib/setterPerformanceCacheService";
import {
  parseSetterMetrics,
  applyCrossDay,
  applyReplyTurnDistribution,
  applyOrphanLost,
  hasChipCounts,
  SetterMetrics,
} from "../useSetterPerformance";
import { warmSpeedToLead } from "../useSpeedToLead";
import type { DailyRow } from "../../lib/aggregateDailyRows";
import { getDMAnalyticsCacheKey } from "../useDMAnalytics";

// =============================================================================
// useDMAnalyticsPrefetch - BACKGROUND DM DATA PREFETCHER
// =============================================================================
// PURPOSE: Silently pre-loads setter IDs + setter performance metrics on app mount,
// so the DM Analytics page renders instantly when the user navigates to it.
//
// ARCHITECTURE:
// - Runs from AppLayout (alongside useCalendarPrefetch)
// - Fire-and-forget: no loading states, no error UI
// - Skips if caches are already warm (no redundant fetches)
// - Fetches setter IDs first, then ALL 3 date ranges in PARALLEL
// - Registers pending fetch Promises so hooks can await them instead of duplicating
// - Stores in the SAME cache keys as useOrgSetterIds/useSetterPerformance
//   so those hooks get instant cache hits
//
// This eliminates the 3-waterfall problem:
// 1. Date range gate (now computed synchronously in DMAnalyticsPage)
// 2. Setter ID fetch (pre-warmed by this hook)
// 3. Metrics fetch (pre-warmed by this hook)
//
// ROLE GATE (2026-07-31): the SETTER-PERFORMANCE caches this hook writes
// (setterIds/setterNames/dailyRows/sessionTimelines/speedToLead/orphan+lost chips)
// are read only by components mounted under DMAnalyticsPage (SetterPerformanceTab,
// DMHealthTab, SetterProofModal, useSetterSessionTimeline, WorkPatternModal).
// Roles whose roleConfig.pages doesn't include the DM Analytics route can never
// mount those consumers, so for them that work is ~9 wasted Supabase calls
// (ensure_setter_performance_fresh x4, get_setter_performance_fast x2,
// get_setter_cross_day_metrics x1, plus setter_daily_performance selects) on a
// 2-core Postgres box.
//
// ⚠️ NOT exclusive, and the gate is deliberately coarse. Step 5 below ALSO warms
// getDMAnalyticsCacheKey(), which hooks/useDMAnalytics.ts reads — and that hook is
// mounted on surfaces gated-out roles CAN reach: ClientPortalPage.tsx:219 (`client`,
// /admin/portal) and RevenuePlanner via ForecastTab under OperationsDashboard
// (`manager`, /admin/dashboard). So gating the whole hook also skips a DM warm those
// two roles could have used. Accepted deliberately, because: (a) useDMAnalytics
// self-fetches live on every mount regardless of cache state, so the only cost is a
// spinner, never a wrong or missing number; (b) this whole hook is behind
// useIdlePrefetchGate (~20s post-load + a browser idle slot, App.tsx:183), so it
// CANNOT warm a first page load anyway — only a later navigation; (c) splitting the
// gate to keep Step 5 while dropping the setter steps means restructuring the
// deferred-promise registration below, which is real regression risk for a spinner.
// Revisit if a client/manager ever reports a slow DM widget on re-navigation.
// (Falsified by /verify Lane 3, 2026-07-31 — the original comment claimed exclusivity.)
//
// Gated below via canWarmSetterPerformance, derived from roleConfig.ts (not a hardcoded role
// string) so a future role with no setter surface is skipped automatically.
// =============================================================================

// The one route that renders a consumer of this hook's caches. Kept as a named
// constant (not inlined) so the "why gate on this path" reasoning above stays
// attached to a single grep-able symbol.
const SETTER_PERFORMANCE_ROUTE = "/admin/dm-analytics";

/** Fetch + parse + cache metrics for a single date range. Returns the parsed metrics. */
async function fetchRangeMetrics(
  orgId: string,
  setterIds: string[],
  dateRange: { start: Date; end: Date }
): Promise<SetterMetrics[]> {
  // dateRange.start/end are already org-tz-aware UTC instants (built with startOfDayInTZ/endOfDayInTZ)
  const rpcParams = {
    p_organization_id: orgId,
    p_setter_ids: setterIds,
    p_start_date: dateRange.start.toISOString(),
    p_end_date: dateRange.end.toISOString(),
  };

  // Fire ensure_fresh (non-blocking)
  supabase!.rpc("ensure_setter_performance_fresh", rpcParams).then(() => {});

  // Fetch main metrics + cross-day in parallel
  const [mainResult, crossDayResult] = await Promise.all([
    supabase!.rpc("get_setter_performance_fast", rpcParams),
    supabase!.rpc("get_setter_cross_day_metrics", rpcParams),
  ]);

  if (mainResult.error) {
    throw mainResult.error;
  }

  const parsed = parseSetterMetrics(mainResult.data || []);

  // D12 TERMINAL HYDRATION RULE: call applyCrossDay UNCONDITIONALLY regardless of
  // crossDayResult.error — on error we pass [] so applyCrossDay sets cross_day_hydrated=true
  // on all rows (zeros → sections hide = current silent-fail UX). Skipping the merge
  // on error would strand cross_day_hydrated=false in warmed cache = permanent skeleton.
  // applyCrossDay also fixes the prefetch drift: it uses parseFloat (canonical hook form)
  // for avg_convo_span_days instead of Number() that the old inline loop used.
  const mergedParsed = applyCrossDay(
    parsed,
    crossDayResult.error ? [] : (crossDayResult.data as Array<Record<string, unknown>> | null)
  );

  setMetricsCache(orgId, dateRange, mergedParsed);
  logger.debug("DM Prefetch: metrics warmed for range", dateRange.start.toISOString().slice(0, 10));

  return mergedParsed;
}

/** Build the cache key for a metrics entry (must match the key in setterPerformanceCacheService). */
function metricsCacheKey(orgId: string, dateRange: { start: Date; end: Date }): string {
  // Use toOrgDateKey (browser-local calendar day) to match useSetterPerformance's pendingKey format.
  const startStr = toOrgDateKey(dateRange.start);
  const endStr = toOrgDateKey(dateRange.end);
  return `metrics_${orgId}_${startStr}_${endStr}`;
}

export function useDMAnalyticsPrefetch(): void {
  const { currentOrganization } = useData();
  const { user } = useAuth();
  const { hasTunnelVision, role } = useRBAC();
  const orgId = currentOrganization?.id ?? null;
  const timezone = currentOrganization?.timezone || "UTC";
  const tunnelGhlId = hasTunnelVision ? (user?.ghlUserId ?? null) : null;
  // Gate: does this role's roleConfig.pages include the only route that consumes
  // this hook's warmed caches? If not, every RPC below is dead weight — e.g. the
  // `client` role's pages are exactly [portal, transactions, installments,
  // calendar, sop-library], zero setter surfaces. `role` is undefined until auth
  // resolves, so this starts false and flips once useRBAC() has a real role.
  const canWarmSetterPerformance = role
    ? roleConfig[role].pages.includes(SETTER_PERFORMANCE_ROUTE)
    : false;
  // Per-org one-shot guard: keyed by orgId (not a bare boolean) so a switch to a
  // second org re-warms. DeferredPrefetchWarmer never unmounts, so a boolean would
  // latch true forever and org #2+ would never get the initial warm.
  const firedForOrgRef = useRef<string | null>(null);
  const lastWarmedRangeRef = useRef<string | null>(null);

  useEffect(() => {
    // Checked FIRST, before firedForOrgRef is touched — if the gate is closed we
    // must not latch the one-shot guard, so a later role change (e.g. Mako's
    // "View As" switching from client to exec) still fires the warm for this org.
    if (!canWarmSetterPerformance) return;
    if (!orgId || !supabase || firedForOrgRef.current === orgId) return;
    firedForOrgRef.current = orgId;

    const prefetch = async () => {
      // Declared outside try so catch can reject deferred promises on error
      const deferredMap = new Map<
        string,
        {
          resolve: (v: SetterMetrics[]) => void;
          reject: (e: unknown) => void;
          dateRange: { start: Date; end: Date };
        }
      >();

      try {
        // Step 0: Compute date ranges FIRST (no I/O) and register deferred promises IMMEDIATELY.
        // This closes the race window: if useSetterPerformance mounts during steps 1-2 below
        // (setter IDs + names + daily rows = 300-700ms), getPendingFetch will return the
        // deferred promise instead of null, so the hook awaits instead of firing duplicate RPCs.
        const now0Pre = new Date().toISOString();
        const partsPre = getPartsInTZ(now0Pre, timezone);

        let tYear = partsPre.year;
        let tMonth = partsPre.month;
        let tDate = partsPre.date;
        if (partsPre.hours < 11) {
          const yest = new Date(partsPre.year, partsPre.month, partsPre.date - 1);
          tYear = yest.getFullYear();
          tMonth = yest.getMonth();
          tDate = yest.getDate();
        }

        const earlyRanges = [
          // today
          {
            start: startOfDayInTZ(tYear, tMonth, tDate, timezone),
            end: endOfDayInTZ(tYear, tMonth, tDate, timezone),
          },
          // this month
          {
            start: startOfDayInTZ(partsPre.year, partsPre.month, 1, timezone),
            end: endOfDayInTZ(tYear, tMonth, tDate, timezone),
          },
          // last month
          (() => {
            const lm = new Date(partsPre.year, partsPre.month - 1, 1);
            const lmEnd = new Date(partsPre.year, partsPre.month, 0);
            return {
              start: startOfDayInTZ(lm.getFullYear(), lm.getMonth(), 1, timezone),
              end: endOfDayInTZ(lmEnd.getFullYear(), lmEnd.getMonth(), lmEnd.getDate(), timezone),
            };
          })(),
        ];

        // Register deferred promises for all uncached ranges
        for (const dr of earlyRanges) {
          if (getMetricsCache<SetterMetrics[]>(orgId, dr)) continue;
          const key = metricsCacheKey(orgId, dr);

          let resolveDeferred: (v: SetterMetrics[]) => void;
          let rejectDeferred: (e: unknown) => void;
          const deferredPromise = new Promise<SetterMetrics[]>((resolve, reject) => {
            resolveDeferred = resolve;
            rejectDeferred = reject;
          });

          setPendingFetch(key, deferredPromise);
          deferredMap.set(key, {
            resolve: resolveDeferred!,
            reject: rejectDeferred!,
            dateRange: dr,
          });
        }

        // Step 1: Warm setter IDs + names in ONE query (were 2 sequential queries before)
        let setterIds = getSetterIdsCache(orgId);
        let hasNames = !!getSetterNamesCache(orgId);

        if (!setterIds || !hasNames) {
          // Single query gets both ghl_user_id AND full_name
          const { data, error } = await supabase!
            .from("organization_members")
            .select("user_id, profiles!inner(ghl_user_id, full_name)")
            .eq("organization_id", orgId)
            .eq("role", "setter")
            .eq("status", "active");

          if (error) throw error;

          const ids: string[] = [];
          const nameMap: Record<string, string> = {};

          for (const row of data || []) {
            const p = row.profiles as unknown as {
              ghl_user_id: string | null;
              full_name: string | null;
            };
            if (p?.ghl_user_id && p.ghl_user_id.length > 0) {
              ids.push(p.ghl_user_id);
              if (p.full_name) {
                nameMap[p.ghl_user_id] = p.full_name;
              }
            }
          }

          if (!setterIds) {
            setterIds = ids;
            setSetterIdsCache(orgId, setterIds);
          }
          if (!hasNames && Object.keys(nameMap).length > 0) {
            setSetterNamesCache(orgId, nameMap);
            hasNames = true;
          }
          logger.debug("DM Prefetch: setter IDs + names warmed", setterIds.length);
        }

        // Tunnel vision: only prefetch data for this setter
        if (tunnelGhlId) {
          setterIds = setterIds.filter((id) => id === tunnelGhlId);
          if (setterIds.length === 0) return; // Current user not in setter list
        }

        if (setterIds.length === 0) {
          // No setters — reject all deferred promises so hooks don't hang
          for (const [, deferred] of deferredMap) {
            deferred.reject(new Error("No setter IDs found"));
          }
          return;
        }

        // Step 1.6: Warm daily rows for current + previous month
        // This enables instant client-side aggregation for ANY date range within these months.
        const now0 = new Date().toISOString();
        const p0 = getPartsInTZ(now0, timezone);
        const currentMonthKey = `${p0.year}-${String(p0.month + 1).padStart(2, "0")}`;
        const prevMonthDate = new Date(p0.year, p0.month - 1, 1);
        const prevMonthKey = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, "0")}`;

        const monthsToWarm = [
          { key: currentMonthKey, start: `${p0.year}-${String(p0.month + 1).padStart(2, "0")}-01` },
          {
            key: prevMonthKey,
            start: `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, "0")}-01`,
          },
        ];

        // Fire ensure_fresh for today (non-blocking)
        supabase!
          .rpc("ensure_setter_performance_fresh", {
            p_organization_id: orgId,
            p_setter_ids: setterIds,
            p_start_date: new Date().toISOString(),
            p_end_date: new Date().toISOString(),
          })
          .then(() => {});

        // Fetch daily rows for both months in parallel
        const dailyRowPromises = monthsToWarm.map(async ({ key, start }) => {
          if (getDailyRowsCache<DailyRow[]>(orgId, key)) return; // already cached

          const lastDay =
            key === currentMonthKey
              ? `${p0.year}-${String(p0.month + 1).padStart(2, "0")}-${String(p0.date).padStart(2, "0")}`
              : (() => {
                  const end = new Date(p0.year, p0.month, 0); // last day of prev month
                  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
                })();

          const { data, error: rowErr } = await supabase!
            .from("setter_daily_performance")
            .select("*")
            .eq("organization_id", orgId)
            .in("setter_id", setterIds)
            .gte("work_date", start)
            .lte("work_date", lastDay)
            .order("work_date", { ascending: true });

          if (!rowErr && data) {
            setDailyRowsCache(orgId, key, data as DailyRow[]);
            logger.debug("DM Prefetch: daily rows warmed for", key, data.length, "rows");
          }
        });

        await Promise.all(dailyRowPromises);

        // Step 2: Resolve deferred promises by firing the actual RPCs.
        // Date ranges were already computed and deferred promises registered in Step 0.
        // Hooks that mounted during steps 1-1.6 above already have the deferred promises.
        const rangesToWarm = earlyRanges;
        const fetchPromises: Promise<void>[] = [];

        for (const [, deferred] of deferredMap) {
          fetchPromises.push(
            fetchRangeMetrics(orgId, setterIds, deferred.dateRange)
              .then((result) => {
                deferred.resolve(result);
              })
              .catch((err) => {
                deferred.reject(err);
                logger.debug("DM Prefetch: metrics RPC error for range, skipping", err);
              })
          );
        }

        // Wait for ALL ranges to complete in parallel
        await Promise.all(fetchPromises);

        // Step 3: Warm reply-turn buckets into the caches written above.
        // MUST run AFTER the Step 2 barrier — the 2-core Postgres box serializes
        // concurrent heavy RPCs, so firing these alongside metrics doubles wall-clock.
        // Ranges run SEQUENTIALLY (for...of with await) for the same reason.
        // Fire-and-forget — Steps 4/5 proceed immediately; the IIFE resolves in background.
        void (async () => {
          for (const dateRange of rangesToWarm) {
            try {
              // Skip if cache is missing or empty — nothing to patch into.
              const cachedRows = getMetricsCache<SetterMetrics[]>(orgId, dateRange);
              if (!cachedRows || cachedRows.length === 0) continue;

              // Dedupe guard: if any row already has a nonzero non-lead bucket,
              // a prior fetch (hook or earlier IIFE iteration) already warmed them.
              const alreadyWarmed = cachedRows.some(
                (r) =>
                  r.reply_turn_0 > 0 ||
                  r.reply_turn_1 > 0 ||
                  r.reply_turn_2 > 0 ||
                  r.reply_turn_3 > 0 ||
                  r.reply_turn_4 > 0 ||
                  r.reply_turn_5 > 0 ||
                  r.reply_turn_6 > 0 ||
                  r.reply_turn_7 > 0 ||
                  r.reply_turn_8 > 0 ||
                  r.reply_turn_9 > 0 ||
                  r.reply_turn_10 > 0 ||
                  r.reply_turn_11_12 > 0 ||
                  r.reply_turn_13_18 > 0 ||
                  r.reply_turn_19plus > 0
              );
              if (alreadyWarmed) continue;

              const { data: rtData, error: rtError } = await supabase!.rpc(
                "get_reply_turn_distribution",
                {
                  p_organization_id: orgId,
                  p_setter_ids: setterIds,
                  p_start_date: dateRange.start.toISOString(),
                  p_end_date: dateRange.end.toISOString(),
                }
              );

              if (rtError || !rtData) continue;

              // Re-read cache — hook may have updated it while the RPC was in-flight.
              const nowRows = getMetricsCache<SetterMetrics[]>(orgId, dateRange);
              if (nowRows) {
                setMetricsCache(
                  orgId,
                  dateRange,
                  applyReplyTurnDistribution(nowRows, rtData as Array<Record<string, unknown>>)
                );
                logger.debug(
                  "DM Prefetch: reply-turn buckets warmed for range",
                  dateRange.start.toISOString().slice(0, 10)
                );
              }
            } catch {
              // One range failing must not kill the rest.
            }
          }
        })();

        // Step 4: Warm session timelines (fire-and-forget, all in parallel)
        const timelinePromises: Promise<void>[] = [];
        for (const dateRange of rangesToWarm) {
          const needsTimeline = setterIds.some(
            (sid) => !getSessionTimelineCache(orgId, dateRange, sid)
          );
          if (!needsTimeline) continue;

          // dateRange.start/end are already org-tz-aware (built with startOfDayInTZ/endOfDayInTZ)
          timelinePromises.push(
            (async () => {
              try {
                const { data: tlData } = await supabase!
                  .rpc("get_setter_session_timeline", {
                    p_organization_id: orgId,
                    p_setter_ids: setterIds,
                    p_start_date: dateRange.start.toISOString(),
                    p_end_date: dateRange.end.toISOString(),
                  })
                  // Bypass PGRST's 1000-row cap — a month × N setters exceeds 1000
                  // rows; without this the warmed cache is silently truncated and
                  // the donuts render with missing sessions.
                  .range(0, 9999);
                if (!tlData) return;
                for (const sid of setterIds) {
                  const tlParsed = (tlData as Record<string, unknown>[])
                    .map((row) => ({
                      setter_id: row.setter_id as string,
                      work_date: row.work_date as string,
                      session_start: row.session_start as string,
                      session_end: row.session_end as string,
                      message_count: Number(row.message_count ?? 0),
                    }))
                    .filter((row) => row.setter_id === sid)
                    .sort(
                      (a, b) =>
                        new Date(a.session_start).getTime() - new Date(b.session_start).getTime()
                    );
                  setSessionTimelineCache(orgId, dateRange, sid, tlParsed);
                }
              } catch {
                // fire-and-forget
              }
            })()
          );
        }
        // Don't await timelines — they're non-blocking. The Step 6 warm below IS
        // gated on their settle (contention doctrine: don't pile more heavy RPCs
        // onto the 2-core box while the timeline batch is in flight).
        void Promise.allSettled(timelinePromises).then(async () => {
          // Step 6: warm the remaining click-path caches (speed_to_lead,
          // orphan/lost chips) so a warm preset-range click fires
          // ZERO RPCs. Per range SEQUENTIALLY — login latency is invisible,
          // contention isn't.
          if (setterIds.length === 0) return;
          for (const dateRange of rangesToWarm) {
            // (a) speed_to_lead — the click-gating RPC (~1.2s warm server-side)
            try {
              await warmSpeedToLead(orgId, setterIds, dateRange, timezone);
            } catch (err) {
              logger.debug("DM Prefetch: speed warm failed for range (non-critical)", err);
            }
            // (b) orphan/lost chip counts — merged INTO the metrics cache rows
            // (they have no cache of their own). All-zero no-error rows refetch
            // on click by design — hasChipCounts can't tell "warmed, truly 0"
            // from "never fetched", and a sentinel field isn't worth shape drift.
            try {
              const rows = getMetricsCache<SetterMetrics[]>(orgId, dateRange);
              if (rows && rows.length > 0 && !hasChipCounts(rows)) {
                const chipParams = {
                  p_organization_id: orgId,
                  p_setter_ghl_user_ids: setterIds,
                  p_start_date: toOrgDateKey(dateRange.start),
                  p_end_date: toOrgDateKey(dateRange.end),
                };
                const [orphanRes, lostRes] = await Promise.all([
                  supabase!.rpc("get_setter_orphan_pre_calls", chipParams),
                  supabase!.rpc("get_setter_lost_pre_calls", chipParams),
                ]);
                // Re-read NOW — an in-flight hook write may have replaced the rows.
                const nowRows = getMetricsCache<SetterMetrics[]>(orgId, dateRange);
                if (nowRows) {
                  setMetricsCache(
                    orgId,
                    dateRange,
                    applyOrphanLost(
                      nowRows,
                      orphanRes.data,
                      !!orphanRes.error,
                      lostRes.data,
                      !!lostRes.error
                    )
                  );
                }
              }
            } catch (err) {
              logger.debug("DM Prefetch: chip warm failed for range (non-critical)", err);
            }
            logger.debug(
              "DM Prefetch: click-path caches warmed for range",
              toOrgDateKey(dateRange.start)
            );
          }
        });

        // Step 5: Warm DM Health tab data (fire-and-forget)
        // useDMAnalytics uses localStorage cache with getCacheKey format.
        // Fire get_dm_analytics_fast for each range — fast RPC (58ms), negligible overhead.
        for (const dateRange of rangesToWarm) {
          // dateRange.end is already org-tz-aware (built with endOfDayInTZ)
          const cacheKey = getDMAnalyticsCacheKey(orgId, dateRange.start, dateRange.end);

          // Skip if already cached in localStorage
          try {
            if (localStorage.getItem(cacheKey)) continue;
          } catch {
            /* ignore */
          }

          // Wrap in async IIFE — supabase.rpc returns PromiseLike, not full Promise
          (async () => {
            try {
              const { data: result } = await supabase!.rpc("get_dm_analytics_fast", {
                p_organization_id: orgId,
                p_start_date: dateRange.start.toISOString(),
                p_end_date: dateRange.end.toISOString(),
              });
              if (result && result.length > 0) {
                const entry = {
                  data: result[0],
                  timestamp: Date.now(),
                  orgId,
                  dateKey: `${dateRange.start.toISOString()}_${dateRange.end.toISOString()}`,
                };
                localStorage.setItem(cacheKey, JSON.stringify(entry));
                logger.debug("DM Prefetch: DM Health warmed for range");
              }
            } catch {
              /* fire-and-forget */
            }
          })();
        }
      } catch (err) {
        // Reject any deferred promises so hooks don't hang forever
        for (const [, deferred] of deferredMap) {
          deferred.reject(err);
        }
        // Fire-and-forget — don't show errors, the page will retry on navigate
        logger.debug("DM Prefetch: failed (non-critical)", err);
      }
    };

    prefetch();
  }, [orgId, timezone, tunnelGhlId, canWarmSetterPerformance]);

  // ---------------------------------------------------------------------------
  // Date-change listener — warms the user-selected range when the picker fires.
  // The 3 hardcoded ranges above (today/this month/last month) don't change —
  // only the user's custom selection needs re-warming here.
  // Debounced 300ms because preset clicks can fire 2-3 events back-to-back.
  // Deduped against lastWarmedRangeRef so identical re-fires are no-ops.
  // This is a SEPARATE path from firedForOrgRef (the per-org one-shot guard above).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Same gate as the mount-time effect above — no point listening for date-range
    // changes to warm a cache this role's pages can never reach a consumer for.
    if (!canWarmSetterPerformance) return;
    if (!orgId || !supabase) return;

    const resolvedOrgId = orgId;
    const resolvedTz = timezone;
    const resolvedTunnelGhlId = tunnelGhlId;

    const fireWarm = async () => {
      try {
        const stored = localStorage.getItem(GLOBAL_DATE_RANGE_KEY);
        if (!stored) return;
        const parsed = JSON.parse(stored);
        if (!parsed.start || !parsed.end) return;

        const start = new Date(parsed.start);
        const end = new Date(parsed.end);
        const rangeKey = `${start.toISOString()}_${end.toISOString()}`;

        if (lastWarmedRangeRef.current === rangeKey) return; // already warm for this range
        lastWarmedRangeRef.current = rangeKey;

        // Fetch setter IDs (from cache if available)
        let setterIds = getSetterIdsCache(resolvedOrgId);
        if (!setterIds) {
          const { data, error } = await supabase!
            .from("organization_members")
            .select("user_id, profiles!inner(ghl_user_id, full_name)")
            .eq("organization_id", resolvedOrgId)
            .eq("role", "setter")
            .eq("status", "active");

          if (error || !data) return;

          const ids: string[] = [];
          const nameMap: Record<string, string> = {};
          for (const row of data) {
            const p = row.profiles as unknown as {
              ghl_user_id: string | null;
              full_name: string | null;
            };
            if (p?.ghl_user_id && p.ghl_user_id.length > 0) {
              ids.push(p.ghl_user_id);
              if (p.full_name) nameMap[p.ghl_user_id] = p.full_name;
            }
          }
          setterIds = ids;
          setSetterIdsCache(resolvedOrgId, setterIds);
          if (Object.keys(nameMap).length > 0) setSetterNamesCache(resolvedOrgId, nameMap);
        }

        if (resolvedTunnelGhlId) {
          setterIds = setterIds.filter((id) => id === resolvedTunnelGhlId);
        }
        if (setterIds.length === 0) return;

        // Build org-tz-aware date range from ISO strings
        const rangeForFetch = {
          start: startOfDayInTZ(start.getFullYear(), start.getMonth(), start.getDate(), resolvedTz),
          end: endOfDayInTZ(end.getFullYear(), end.getMonth(), end.getDate(), resolvedTz),
        };

        // Warm setter metrics for the selected range
        fetchRangeMetrics(resolvedOrgId, setterIds, rangeForFetch).catch(() => {
          /* fire-and-forget */
        });

        // Warm DM Health tab data for the selected range
        const cacheKey = getDMAnalyticsCacheKey(
          resolvedOrgId,
          rangeForFetch.start,
          rangeForFetch.end
        );
        try {
          if (localStorage.getItem(cacheKey)) return; // already cached
        } catch {
          /* ignore */
        }
        (async () => {
          try {
            const { data: result } = await supabase!.rpc("get_dm_analytics_fast", {
              p_organization_id: resolvedOrgId,
              p_start_date: rangeForFetch.start.toISOString(),
              p_end_date: rangeForFetch.end.toISOString(),
            });
            if (result && result.length > 0) {
              const entry = {
                data: result[0],
                timestamp: Date.now(),
                orgId: resolvedOrgId,
                dateKey: `${rangeForFetch.start.toISOString()}_${rangeForFetch.end.toISOString()}`,
              };
              localStorage.setItem(cacheKey, JSON.stringify(entry));
              logger.debug("DM Prefetch: DM Health warmed for user-selected range");
            }
          } catch {
            /* fire-and-forget */
          }
        })();
      } catch {
        /* fire-and-forget */
      }
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fireWarm, 300);
    };

    window.addEventListener(DATE_RANGE_CHANGE_EVENT, debounced);
    return () => {
      window.removeEventListener(DATE_RANGE_CHANGE_EVENT, debounced);
      if (timer) clearTimeout(timer);
    };
  }, [orgId, timezone, tunnelGhlId, canWarmSetterPerformance]);
}
